import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Mail, FileText, BookOpen, AlertTriangle, ChevronLeft, ChevronRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { toast } from "sonner";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { api } from "../lib/api";

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.6, ease: [0.22, 1, 0.36, 1] } }),
};

// Canonical category order — keep in sync with Library.jsx CATEGORY_ORDER.
const CATEGORY_ORDER = [
  "Managing Performance & Accountability",
  "Emotional & Human Moments",
  "Team Dynamics & Relationships",
  "Communication & Transparency",
  "Identity, Authority & Leadership Presence",
  "Career & Transitions",
  "Managing Up & Organizational Reality",
  "Self-Management for Managers",
  "Managing Change",
];

// Per-category accent — kept in lockstep with Library.jsx CATEGORY_ACCENT.
const CATEGORY_ACCENT = {
  "Managing Performance & Accountability": { edge: "var(--mme-teal)",     wash: "rgba(8, 114, 128, 0.045)" },
  "Emotional & Human Moments":              { edge: "var(--mme-plum)",     wash: "rgba(136, 98, 119, 0.045)" },
  "Team Dynamics & Relationships":          { edge: "var(--mme-sage)",     wash: "rgba(74, 158, 139, 0.045)" },
  "Communication & Transparency":           { edge: "var(--mme-teal-soft)",wash: "rgba(63, 142, 153, 0.045)" },
  "Identity, Authority & Leadership Presence": { edge: "var(--mme-coral)", wash: "rgba(244, 125, 95, 0.045)" },
  "Career & Transitions":                   { edge: "#1F4D5A",             wash: "rgba(31, 77, 90, 0.045)" },
  "Managing Up & Organizational Reality":   { edge: "var(--mme-gray)",     wash: "rgba(108, 109, 112, 0.045)" },
  "Self-Management for Managers":           { edge: "var(--mme-plum-soft)",wash: "rgba(168, 133, 151, 0.045)" },
  "Managing Change":                        { edge: "var(--mme-sage-deep)",wash: "rgba(53, 122, 107, 0.045)" },
};

// Manager testimonials — surfaced in the auto-scrolling marquee. Order
// alternates emotional-support quotes with practical-utility quotes so
// browsers see both angles no matter where they enter the loop.
const TESTIMONIALS = [
  {
    body: (
      <>
        I am so grateful I stumbled on your Instagram account, which then led me to your website and the manager toolkit. I&apos;ve been looking for something that I can use across multiple departments with different team configurations and manager needs. The PDFs are really useful and are easy to understand with simple to implement steps.
        {" "}
        <em className="italic">Honestly, this discovery made my day/week/month.</em> So glad to have this resource as part of my tool kit!
      </>
    ),
    attribution: "Vice President for Human Resources, retreat & conference services",
  },
  {
    body: (
      <>Wow, this toolkit was the immediate resource to help me in tough situations. <em className="italic">I didn&apos;t know how much I needed this!</em></>
    ),
    attribution: "Manager, healthcare",
  },
  {
    body: (
      <>I struggle in the emotional parts of management, your support has <em className="italic">helped me think through these situations more proactively.</em></>
    ),
    attribution: "Manager, data analytics",
  },
  {
    body: (
      <>I printed out a PDF from the toolkit for a mentoring session this week that discussed managing people with more experience. We used this to guide our conversation, and <em className="italic">the structure was a valuable add!</em></>
    ),
    attribution: "Manager, marketing",
  },
  {
    body: (
      <>My favorite part of the toolkit is the <em className="italic">sample phrases</em> to use in your conversations. It is not always easy to find the most appropriate, intentional words in the moment and this section helps me prepare.</>
    ),
    attribution: "Manager, undisclosed industry",
  },
  {
    body: (
      <>The immediate access to context and a structure for how to approach different situations is so helpful. <em className="italic">The framing also helped a situation I was facing feel less personal</em> because I realized it was common.</>
    ),
    attribution: "Manager, undisclosed industry",
  },
];


// Horizontal-scroll preview row for the Landing page. (Component retained
// in case we re-introduce per-category scroll strips later; not currently
// used — the active preview uses one summary card per category instead.)
// eslint-disable-next-line no-unused-vars
function PreviewRow({ items, accent }) {
  const scrollerRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollerRef.current;
    if (!el) return undefined;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [items.length]);

  const step = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const cardEl = el.querySelector("[data-card]");
    const cardW = cardEl ? cardEl.offsetWidth + 20 : el.clientWidth * 0.4;
    el.scrollBy({ left: dir * cardW, behavior: "smooth" });
  };

  return (
    <div className="relative">
      <button
        type="button"
        aria-label="Scroll left"
        onClick={() => step(-1)}
        className={`hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-10 h-10 rounded-full transition-all duration-200 ${
          canLeft ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        style={{
          background: "#ffffff",
          border: `1px solid ${accent.edge}`,
          color: accent.edge,
          boxShadow: "0 6px 20px rgba(31, 85, 89, 0.10)",
          marginLeft: "-14px",
        }}
      >
        <ChevronLeft size={18} strokeWidth={2.4} />
      </button>
      <button
        type="button"
        aria-label="Scroll right"
        onClick={() => step(1)}
        className={`hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-10 h-10 rounded-full transition-all duration-200 ${
          canRight ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        style={{
          background: "#ffffff",
          border: `1px solid ${accent.edge}`,
          color: accent.edge,
          boxShadow: "0 6px 20px rgba(31, 85, 89, 0.10)",
          marginRight: "-14px",
        }}
      >
        <ChevronRight size={18} strokeWidth={2.4} />
      </button>

      <div
        ref={scrollerRef}
        className="flex gap-5 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 -mx-2 px-2"
        style={{ scrollbarWidth: "thin" }}
      >
        {items.map((s, i) => (
          <motion.div
            key={s.slug}
            data-card
            variants={fadeUp}
            custom={i}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            className="shrink-0 snap-start w-[88%] sm:w-[46%] lg:w-[31%] xl:w-[30%] relative overflow-hidden"
            style={{
              background: "#FBF6E8",
              border: "1px solid var(--mek-border)",
              borderRadius: "10px",
              minHeight: "260px",
              padding: "26px 24px 26px 30px",
            }}
            data-testid={`scenario-card-${s.slug}`}
          >
            <span aria-hidden="true" className="absolute left-0 top-0 bottom-0" style={{ width: "5px", background: accent.edge }} />
            <div className="relative flex flex-col h-full">
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] mb-3" style={{ color: accent.edge }}>
                Included in the kit
              </p>
              <p className="font-serif text-[22px] leading-tight mb-3" style={{ color: "var(--mek-text)" }}>
                {s.title}
              </p>
              <p className="text-prose text-sm leading-relaxed">{s.preview}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

const HERO_IMG = "https://images.unsplash.com/photo-1758691737207-e75821e080cb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHw0fHx3YXJtJTIwY2FsbSUyMG9mZmljZSUyMGNvbnZlcnNhdGlvbnxlbnwwfHx8fDE3NzgzNzM0NjF8MA&ixlib=rb-4.1.0&q=85";
const SECONDARY_IMG = "https://images.unsplash.com/photo-1529682427908-9da107d649f0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxjYWxtJTIwaGFuZHMlMjBob2xkaW5nJTIwY29mZmVlJTIwY3VwJTIwd2FybXxlbnwwfHx8fDE3NzgzNzM0ODJ8MA&ixlib=rb-4.1.0&q=85";

// Format the backend's numeric price for display. Uses "$65" when the
// amount is whole-dollar (avoids "$65.00" clutter on buttons) and "$64.50"
// when there are cents. Returns "..." while /config is still loading so
// we never flash an outdated hardcoded price at the visitor.
const formatPrice = (n) => {
  if (typeof n !== "number") return "…";
  return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
};

export default function Landing() {
  const [scenarios, setScenarios] = useState([]);
  const [email, setEmail] = useState("");
  const [loadingCheckout, setLoadingCheckout] = useState(false);
  // "Buy one, send one" gift-along-with-purchase state. The whole UI is
  // gated behind giftOfferActive (driven by GIFT_OFFER_ACTIVE on backend).
  const [giftOfferActive, setGiftOfferActive] = useState(true);
  const [includeGift, setIncludeGift] = useState(false);
  const [giftEmail, setGiftEmail] = useState("");
  const [giftNote, setGiftNote] = useState("");
  const [senderName, setSenderName] = useState("");
  const GIFT_NOTE_MAX = 280;
  // A/B headline experiment. `abVariant` is null when no experiment is
  // active, or carries `{key, headline_main, headline_accent}` for the
  // assigned variant. Assignment is sticky per browser via localStorage.
  const [abVariant, setAbVariant] = useState(null);
  // Price is the ONLY source of truth from the backend — never hardcoded.
  // If /config hasn't answered yet, we render a bland dash so the button
  // never briefly flashes an old price. Backend authoritative value flows
  // through everywhere ($ hero button, pricing headline, buy button).
  const [priceUsd, setPriceUsd] = useState(null);

  useEffect(() => {
    api.get("/scenarios").then((r) => setScenarios(r.data)).catch(() => {});
    api
      .get("/config")
      .then((r) => {
        setGiftOfferActive(Boolean(r.data?.gift_offer_active));
        if (typeof r.data?.price_usd === "number") setPriceUsd(r.data.price_usd);
      })
      .catch(() => {});

    // Fetch the active A/B experiment. If found, deterministically assign a
    // variant (50/50) and persist it in localStorage so the visitor sees the
    // same hero on every return visit. We track the impression AFTER we
    // know the variant so the event is properly tagged.
    api
      .get("/experiment/active")
      .then((r) => {
        const exp = r.data?.experiment;
        if (!exp || !Array.isArray(exp.variants) || exp.variants.length < 2) {
          // No experiment — log a vanilla impression.
          api.post("/track", { kind: "landing_view" }).catch(() => {});
          return;
        }
        const storageKey = `mek_ab_${exp.key}`;
        let assigned = localStorage.getItem(storageKey);
        const validKeys = exp.variants.map((v) => v.key);
        if (!assigned || !validKeys.includes(assigned)) {
          assigned = exp.variants[Math.floor(Math.random() * exp.variants.length)].key;
          try {
            localStorage.setItem(storageKey, assigned);
          } catch (e) {
            /* localStorage blocked — variant stays in-memory only */
          }
        }
        const variant = exp.variants.find((v) => v.key === assigned) || exp.variants[0];
        setAbVariant(variant);
        api
          .post("/track", { kind: "landing_view", ab_variant: variant.key })
          .catch(() => {});
      })
      .catch(() => {
        // Endpoint hiccup — still record an untagged landing view.
        api.post("/track", { kind: "landing_view" }).catch(() => {});
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const groupedScenarios = useMemo(() => {
    const byCat = new Map();
    for (const s of scenarios) {
      const list = byCat.get(s.category) || [];
      list.push(s);
      byCat.set(s.category, list);
    }
    for (const list of byCat.values()) {
      list.sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
    }
    return CATEGORY_ORDER
      .filter((c) => byCat.has(c))
      .map((c) => [c, byCat.get(c)]);
  }, [scenarios]);

  const startCheckout = async () => {
    if (!email || !email.includes("@")) {
      toast.error("We need a real email to send your access link.");
      return;
    }
    if (includeGift) {
      if (!giftEmail || !giftEmail.includes("@")) {
        toast.error("Add the email of the person you're sharing this with.");
        return;
      }
      if (giftEmail.trim().toLowerCase() === email.trim().toLowerCase()) {
        toast.error("Gift email needs to be different from your own.");
        return;
      }
    }
    setLoadingCheckout(true);
    try {
      const payload = { email, origin_url: window.location.origin };
      if (abVariant?.key) payload.ab_variant = abVariant.key;
      if (includeGift) {
        payload.gift_to_email = giftEmail.trim();
        if (senderName.trim()) payload.sender_name = senderName.trim();
        if (giftNote.trim()) payload.gift_note = giftNote.trim();
      }
      const { data } = await api.post("/checkout/session", payload);
      window.location.href = data.url;
    } catch (e) {
      toast.error("Couldn't start checkout. Try again in a moment.");
      setLoadingCheckout(false);
    }
  };

  return (
    <div className="App" data-testid="landing-page">
      <Nav />

      {/* HERO */}
      <section
        className="relative overflow-hidden"
        style={{ minHeight: "min(86vh, 820px)" }}
      >
        {/* Background image */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${HERO_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center 30%",
          }}
          aria-hidden
        />
        {/* Teal tint overlay */}
        <div
          className="absolute inset-0"
          style={{ background: "rgba(31, 85, 89, 0.78)", mixBlendMode: "multiply" }}
          aria-hidden
        />
        {/* Soft vignette for legibility */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(31,85,89,0.25) 0%, rgba(31,85,89,0.45) 60%, rgba(31,85,89,0.65) 100%)",
          }}
          aria-hidden
        />

        {/* Content */}
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 pt-24 md:pt-36 pb-20 md:pb-32">
          <motion.div variants={fadeUp} initial="hidden" animate="show" className="max-w-3xl">
            <p
              data-testid="hero-eyebrow"
              style={{
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: "var(--mme-gold)",
                border: "0",
                margin: "0 0 2rem 0",
                padding: 0,
              }}
            >
              A Manage(Meant) Excellence product
            </p>
            <h1
              className="h-display text-4xl sm:text-5xl lg:text-[64px] mb-8 leading-[1.08]"
              data-testid="hero-headline"
              data-ab-variant={abVariant?.key || "default"}
              style={{ color: "#ffffff" }}
            >
              {abVariant?.headline_main || "The manager toolkit"}
              <br />
              <em
                className="italic font-light inline-block"
                style={{
                  color: "#ffffff",
                  background: "var(--mme-gold-deep)",
                  padding: "0.12em 0.35em 0.18em",
                  marginTop: "0.15em",
                  lineHeight: 1.0,
                }}
              >
                {abVariant?.headline_accent || "nobody handed you."}
              </em>
            </h1>
            <p
              className="text-base md:text-lg max-w-xl mb-10 leading-relaxed"
              data-testid="hero-subhead"
              style={{ color: "rgba(245, 239, 227, 0.92)", fontWeight: 300 }}
            >
              Practical support for the uncomfortable, emotional, high-pressure, and people-centered moments of management, including scripts, reflection prompts, and scenario-based guidance for the moments no one trained you for (and that weren't in the job description).
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a href="#pricing" className="btn-primary" data-testid="hero-cta-primary">
                Get Toolkit 3: The Moments — {formatPrice(priceUsd)}
                <ArrowRight size={16} />
              </a>
              <a
                href="#inside"
                className="btn-secondary"
                style={{ color: "#ffffff", borderColor: "rgba(255,255,255,0.6)" }}
                data-testid="hero-cta-secondary"
              >
                See what's inside
              </a>
            </div>
            <div
              className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-12 text-sm"
              style={{ color: "rgba(245, 239, 227, 0.75)" }}
            >
              <span className="flex items-center gap-2"><Check size={14} style={{ color: "var(--mme-gold)" }} /> Instant access</span>
              <span className="flex items-center gap-2"><Check size={14} style={{ color: "var(--mme-gold)" }} /> Access to updates</span>
              <span className="flex items-center gap-2"><Check size={14} style={{ color: "var(--mme-gold)" }} /> Option to download as PDF</span>
            </div>

            {/* Discovery ribbon — for people landing here from Instagram / word-of-mouth
                who don't yet know this is part of the wider Manager Operating System.
                Two soft-colored buttons link out to the OS site (teal for Toolkit 1
                "The Role", maroon for Toolkit 2 "The People") so this feels like a
                helpful signpost, not a hard cross-sell. */}
            <div
              className="mt-10 pt-8"
              style={{ borderTop: "1px solid rgba(245, 239, 227, 0.18)" }}
              data-testid="hero-os-ribbon"
            >
              <p
                className="text-[11px] font-bold uppercase tracking-[0.2em] mb-4"
                style={{ color: "rgba(245, 239, 227, 0.7)" }}
              >
                This is Toolkit 3 in the Manager Operating System
              </p>
              <p
                className="text-sm mb-5 max-w-xl leading-relaxed"
                style={{ color: "rgba(245, 239, 227, 0.82)" }}
              >
                Landed here first? Explore the rest of the operating system — a fuller framework for building your management practice.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://operatingsystem.managemeantexcellence.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-medium transition"
                  style={{
                    background: "#087280",
                    color: "#ffffff",
                    letterSpacing: "0.02em",
                  }}
                  data-testid="hero-toolkit-1-btn"
                >
                  Toolkit 1: The Role
                  <ArrowRight size={14} />
                </a>
                <a
                  href="https://operatingsystem.managemeantexcellence.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-medium transition"
                  style={{
                    background: "#8B6E7B",
                    color: "#ffffff",
                    letterSpacing: "0.02em",
                  }}
                  data-testid="hero-toolkit-2-btn"
                >
                  Toolkit 2: The People
                  <ArrowRight size={14} />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="py-20 md:py-28" style={{ background: "var(--mek-bg-2)" }} data-testid="built-for-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <h2 className="h-section text-4xl md:text-5xl mb-6 leading-tight" style={{ color: "var(--mme-teal)" }}>
              For the manager doing <em className="italic">the invisible work.</em>
            </h2>
            <p className="text-prose text-lg max-w-md">
              You're already carrying the parts of management no one talks about — the emotions, dynamics, tension, uncertainty, and hard conversations. This toolkit offers practical structure for those moments: grounded guidance, real language, and support you can actually use.
            </p>
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-2 gap-4">
            {[
              ["First-time managers", "Preparing for the parts of management no one fully explains.", "var(--mme-coral)", "rgba(244, 125, 95, 0.045)"],
              ["Managers carrying hard conversations", "Navigating burnout, grief, conflict, resignations, and emotionally complex moments.", "var(--mme-plum)", "rgba(136, 98, 119, 0.045)"],
              ["Experienced leaders", "Practical structure for the hardest, most human parts of leadership.", "#1F4D5A", "rgba(31, 77, 90, 0.045)"],
              ["HR & people partners", "Helping managers navigate the realities people bring to work.", "var(--mme-sage)", "rgba(74, 158, 139, 0.045)"],
            ].map(([h, s, edge, wash], i) => (
              <motion.div
                key={h}
                variants={fadeUp}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                className="relative overflow-hidden transition-all duration-200 hover:-translate-y-0.5"
                style={{
                  background: "#FBF6E8",
                  border: "1px solid var(--mek-border)",
                  borderRadius: "10px",
                  padding: "26px 24px 26px 30px",
                }}
                data-testid={`audience-card-${i}`}
              >
                <span aria-hidden="true" className="absolute left-0 top-0 bottom-0" style={{ width: "5px", background: edge }} />
                <span aria-hidden="true" className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none" style={{ background: wash }} />
                <div className="relative">
                  <p className="font-serif text-xl mb-2 leading-tight" style={{ color: "var(--mek-text)" }}>{h}</p>
                  <p className="text-prose text-sm leading-relaxed">{s}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PEEK INSIDE */}
      <section id="inside" className="py-24 md:py-32 relative overflow-hidden" style={{ background: "var(--mek-bg-2)" }} data-testid="peek-inside-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 mb-16 md:mb-20">
            <div className="md:col-span-7">
              <h2 className="h-section text-4xl md:text-5xl">
                A peek at the pages <em className="italic">you'll actually reach for.</em>
              </h2>
            </div>
            <div className="md:col-span-5 md:pt-3">
              <p className="text-prose text-lg">
                Every scenario follows the same steady, repeatable structure, because hard moments are easier to navigate when you have language, reminders, and practical next steps to lean on. Save, print, or revisit the pages that fit the moment.
              </p>
              <div className="flex flex-wrap gap-2 mt-5">
                <span className="pill pill-stone">Scenario overviews</span>
                <span className="pill pill-stone">What to do in the moment</span>
                <span className="pill pill-stone">Helpful language</span>
                <span className="pill pill-stone">Common pitfalls (what to avoid)</span>
                <span className="pill pill-stone">Manager reminder</span>
                <span className="pill pill-stone">Follow through</span>
              </div>
            </div>
          </div>

          {/* Mockup grid */}
          <div className="grid lg:grid-cols-12 gap-6 lg:gap-8">
            {/* LEFT: Feature scenario page mockup — real content from "When Someone Is Burned Out" */}
            <div className="lg:col-span-7">
              <div
                className="relative bg-white border rounded-2xl p-8 md:p-10 shadow-[0_20px_60px_rgba(31,85,89,0.08)]"
                style={{ borderColor: "var(--mek-border)" }}
                data-testid="mock-scenario-page"
              >
                <div className="flex items-center justify-between mb-7 pb-6 border-b" style={{ borderColor: "var(--mek-border)" }}>
                  <p className="eyebrow" style={{ color: "var(--mme-plum)" }}>Emotional &amp; human moments · Scenario 02</p>
                  <p className="text-xs font-mono" style={{ color: "var(--mek-text-3)" }}>From the toolkit</p>
                </div>

                <h3 className="font-serif text-3xl md:text-4xl leading-tight mb-5" style={{ color: "var(--mek-text)" }}>
                  When Someone Is Burned Out
                </h3>

                <p className="eyebrow mb-3" style={{ color: "var(--mme-plum)" }}>What may really be happening</p>
                <div className="text-prose leading-relaxed mb-8 space-y-3">
                  <p>Burnout does not always look dramatic. Sometimes it looks like lower energy, shorter patience, missed details, emotional flatness, or someone who used to go above and beyond suddenly doing the minimum needed to get through the day.</p>
                  <p>Your role is not to diagnose or "fix" someone. Your role is to notice patterns, create safety, support where appropriate, and still maintain clarity around work expectations.</p>
                </div>

                <p className="eyebrow mb-4" style={{ color: "var(--mme-teal-deep)" }}>What to do in the moment</p>
                <ol className="space-y-3 mb-2">
                  {[
                    ["Notice patterns, not one bad day", "Look for sustained shifts in energy, communication, or work quality."],
                    ["Lead with observation, not assumption", "Avoid deciding what is wrong before you ask."],
                    ["Create room without forcing vulnerability", "Offer support without demanding disclosure."],
                    ["Balance empathy with clarity", "Care and accountability can exist together."],
                    ["Think systems, not just individuals", "Ask what in the environment may be contributing."],
                  ].map(([h, s], i) => (
                    <li key={i} className="flex gap-4 leading-relaxed">
                      <span
                        className="shrink-0 font-semibold tabular-nums"
                        style={{ color: "var(--mme-teal-deep)", minWidth: "1.75rem" }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span>
                        <span className="font-semibold" style={{ color: "var(--mek-text)" }}>{h}</span>{" "}
                        <span className="text-prose">— {s}</span>
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            {/* RIGHT: stacked smaller mock cards — Helpful language / Avoid this / Manager reminder / Follow through */}
            <div className="lg:col-span-5 flex flex-col gap-6">
              {/* Helpful language */}
              <div
                className="border rounded-2xl p-7 md:p-8 shadow-[0_20px_60px_rgba(31,85,89,0.06)]"
                style={{ background: "rgba(42, 110, 115, 0.08)", borderColor: "var(--mek-border)" }}
                data-testid="mock-helpful-language-card"
              >
                <p className="eyebrow mb-4" style={{ color: "var(--mme-teal-deep)" }}>Helpful language</p>
                <ul className="space-y-3 text-prose leading-relaxed">
                  <li className="flex gap-3"><span className="shrink-0 mt-2.5 w-1.5 h-1.5 rounded-full" style={{ background: "var(--mme-teal)" }} /><span>"I wanted to check in. I've noticed you seem more stretched lately."</span></li>
                  <li className="flex gap-3"><span className="shrink-0 mt-2.5 w-1.5 h-1.5 rounded-full" style={{ background: "var(--mme-teal)" }} /><span>"You don't need to share anything personal, but is there anything getting in the way of work feeling manageable right now?"</span></li>
                  <li className="flex gap-3"><span className="shrink-0 mt-2.5 w-1.5 h-1.5 rounded-full" style={{ background: "var(--mme-teal)" }} /><span>"What feels hardest right now?"</span></li>
                </ul>
              </div>

              {/* Avoid this */}
              <div
                className="border rounded-2xl p-7 md:p-8 shadow-[0_20px_60px_rgba(31,85,89,0.06)]"
                style={{ background: "rgba(140, 99, 120, 0.08)", borderColor: "var(--mek-border)" }}
                data-testid="mock-avoid-card"
              >
                <p className="eyebrow mb-4" style={{ color: "var(--mme-plum)" }}>Avoid this</p>
                <ul className="space-y-2.5 leading-relaxed">
                  {["\"You just need to manage your time better.\"", "\"Everyone is stressed.\"", "\"You seem unmotivated.\"", "\"You need to push through.\""].map((q, i) => (
                    <li key={i} className="flex gap-3" style={{ color: "var(--mek-text)" }}>
                      <span
                        aria-hidden="true"
                        className="shrink-0 mt-2"
                        style={{ width: "10px", height: "10px", background: "var(--mme-plum)", transform: "rotate(45deg)", borderRadius: "1px" }}
                      />
                      <span>{q}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Manager reminder */}
              <div
                className="rounded-2xl p-7 md:p-8 shadow-[0_20px_60px_rgba(31,85,89,0.10)]"
                style={{ background: "var(--mme-teal)", color: "#ffffff" }}
                data-testid="mock-reminder-card"
              >
                <p className="eyebrow mb-4" style={{ color: "rgba(245, 239, 227, 0.85)" }}>Manager reminder</p>
                <p className="text-base md:text-lg font-medium leading-relaxed" style={{ color: "#ffffff" }}>
                  Burnout isn't just overwhelm. Sometimes it is sustained over-functioning meeting depleted capacity. Your job is not to rescue someone — it is to notice, respond, and lead with steadiness.
                </p>
              </div>

              {/* Follow through */}
              <div
                className="bg-white border rounded-2xl p-7 md:p-8 shadow-[0_20px_60px_rgba(31,85,89,0.06)]"
                style={{ borderColor: "var(--mek-border)" }}
                data-testid="mock-followup-card"
              >
                <p className="eyebrow mb-4" style={{ color: "var(--mme-teal-deep)" }}>Follow through</p>
                <div className="divide-y" style={{ borderColor: "rgba(42, 110, 115, 0.18)" }}>
                  {[
                    ["Within 48 hours", "Check back in briefly. \"How are things feeling this week?\""],
                    ["This week", "Look for small, reasonable adjustments — workload clarity, reprioritization, temporary flexibility."],
                    ["Ongoing", "Consistency builds trust. Don't pretend the conversation never happened."],
                  ].map(([when, what], i) => (
                    <div key={i} className={`grid grid-cols-12 gap-3 ${i === 0 ? "pb-4" : "py-4"} ${i === 2 ? "pb-0" : ""}`}>
                      <p className="col-span-4 font-semibold leading-snug" style={{ color: "var(--mme-teal-deep)" }}>{when}</p>
                      <p className="col-span-8 text-prose text-sm leading-relaxed">{what}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Format strip */}
          <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 max-w-3xl mx-auto text-center">
            {[
              ["Web library", "Scenario-based playbooks, browseable on any device"],
              ["Downloadable PDF", "Full kit + per-scenario printables"],
              ["Print-friendly", "Take a page into the room with you"],
            ].map(([h, s]) => (
              <div key={h} className="px-4">
                <p className="font-serif text-xl mb-1" style={{ color: "var(--mek-text)" }}>{h}</p>
                <p className="text-prose text-sm">{s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SCENARIOS */}
      <section id="scenarios" className="py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 mb-14">
            <div className="md:col-span-7">
              <h2 className="h-section text-4xl md:text-5xl">
                The situations no one gave you <em className="italic">a script for.</em>
              </h2>
            </div>
            <div className="md:col-span-5 md:pt-2">
              <p className="text-prose text-lg">
                Each scenario follows the same repeatable structure: what's happening, what to say, what to avoid, when to escalate, and how to follow up.
              </p>
              <p className="text-prose text-sm mt-3" style={{ color: "var(--mek-text-3)" }}>
                {scenarios.length} scenarios across 8 categories.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {groupedScenarios.map(([category, items], gi) => {
              const accent = CATEGORY_ACCENT[category] || { edge: "var(--mme-teal)" };
              return (
                <motion.div
                  key={category}
                  variants={fadeUp}
                  custom={gi}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, margin: "-80px" }}
                  className="relative overflow-hidden"
                  style={{
                    background: "#FBF6E8",
                    border: "1px solid var(--mek-border)",
                    borderRadius: "10px",
                    padding: "32px 30px 28px 36px",
                  }}
                  data-testid={`landing-category-${gi}`}
                >
                  <span aria-hidden="true" className="absolute left-0 top-0 bottom-0" style={{ width: "5px", background: accent.edge }} />
                  <div className="relative">
                    <p
                      className="text-[11px] font-bold uppercase tracking-[0.22em] mb-3"
                      style={{ color: accent.edge }}
                    >
                      {category}
                    </p>
                    <p className="font-serif text-[26px] leading-tight mb-5" style={{ color: "var(--mek-text)" }}>
                      {items.length} {items.length === 1 ? "scenario" : "scenarios"} you can reach for
                    </p>
                    <ul className="space-y-2.5">
                      {items.map((s) => (
                        <li key={s.slug} className="flex gap-3 leading-relaxed" style={{ color: "var(--mek-text)" }}>
                          <span
                            className="shrink-0 mt-2 w-1.5 h-1.5 rounded-full"
                            style={{ background: accent.edge }}
                            aria-hidden="true"
                          />
                          <span className="text-[15px]">{s.title}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <h2 className="h-section text-4xl md:text-5xl mb-16 max-w-2xl">
            Support for the moments that weren't in the job description.
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Mail, title: "1. Instant access", body: "One-time purchase. Your link arrives in minutes — and again whenever you need to come back. No password to lose, no portal to remember.", edge: "var(--mme-teal)" },
              { icon: BookOpen, title: "2. Open what fits today", body: "Two clicks to the scenario that matches the moment. No search hunt, no scroll fatigue, even when the day already feels heavy.", edge: "var(--mme-plum)" },
              { icon: FileText, title: "3. Carry it with you", body: "Print-friendly pages, clean mobile reading. Save the scenarios that fit your team and bring them into your next conversation.", edge: "#1F4D5A" },
            ].map((c, i) => (
              <motion.div
                key={c.title}
                variants={fadeUp}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="relative overflow-hidden"
                style={{
                  background: "#FBF6E8",
                  border: "1px solid var(--mek-border)",
                  borderRadius: "10px",
                  padding: "30px 28px 30px 34px",
                }}
                data-testid={`how-card-${i}`}
              >
                <span aria-hidden="true" className="absolute left-0 top-0 bottom-0" style={{ width: "5px", background: c.edge }} />
                <div className="relative">
                  <c.icon size={26} strokeWidth={1.4} style={{ color: c.edge }} />
                  <p className="font-serif text-2xl mt-5 mb-3 leading-tight" style={{ color: "var(--mek-text)" }}>{c.title}</p>
                  <p className="text-prose text-sm leading-relaxed">{c.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — auto-scrolling marquee, pauses on hover */}
      <section
        className="py-24 md:py-28 overflow-hidden"
        style={{ background: "var(--mek-bg)" }}
        data-testid="testimonial-section"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-12 mb-12 text-center">
          <p
            className="text-[11px] font-bold uppercase tracking-[0.22em]"
            style={{ color: "var(--mek-text-3)" }}
          >
            What managers are saying
          </p>
        </div>

        {/* Marquee — CSS-only, seamless loop via duplicated content */}
        <style>{`
          @keyframes mek-marquee {
            from { transform: translateX(0); }
            to   { transform: translateX(-50%); }
          }
          .mek-marquee-track {
            display: flex;
            gap: 28px;
            width: max-content;
            animation: mek-marquee 72s linear infinite;
            will-change: transform;
          }
          .mek-marquee-wrap:hover .mek-marquee-track { animation-play-state: paused; }
          @media (prefers-reduced-motion: reduce) {
            .mek-marquee-track { animation: none; }
          }
        `}</style>

        <div
          className="mek-marquee-wrap relative"
          data-testid="testimonial-marquee"
          style={{
            maskImage:
              "linear-gradient(to right, transparent 0, black 6%, black 94%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(to right, transparent 0, black 6%, black 94%, transparent 100%)",
          }}
        >
          <div className="mek-marquee-track">
            {[...TESTIMONIALS, ...TESTIMONIALS].map((t, i) => (
              <figure
                key={i}
                className="rounded-2xl px-8 py-8 flex flex-col justify-between"
                style={{
                  background: "#ffffff",
                  border: "1px solid var(--mek-border)",
                  width: "380px",
                  minHeight: "260px",
                }}
                data-testid={`testimonial-card-${i}`}
              >
                <span
                  aria-hidden="true"
                  className="font-serif italic block mb-2"
                  style={{
                    fontSize: "44px",
                    lineHeight: 0.4,
                    color: "var(--mme-gold)",
                  }}
                >
                  &ldquo;
                </span>
                <blockquote
                  className="font-serif text-lg leading-snug flex-1"
                  style={{ color: "var(--mek-text)" }}
                >
                  {t.body}
                </blockquote>
                <figcaption
                  className="text-xs mt-6"
                  style={{ color: "var(--mek-text-2)" }}
                >
                  — {t.attribution}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-24 md:py-32" style={{ background: "var(--mek-bg-2)" }}>
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="h-section text-4xl md:text-5xl mb-6">
            {formatPrice(priceUsd)} for the toolkit <em className="italic">you'll actually reach for.</em>
          </h2>
          <p className="text-prose text-lg mb-10 max-w-xl mx-auto">
            One-time purchase. Access anytime. New scenarios added over time.
          </p>

          {giftOfferActive && (
            <div
              className="max-w-xl mx-auto mb-5 flex items-center gap-3 px-5 py-3 rounded-full"
              style={{
                background: "var(--mme-gold)",
                border: "1px solid var(--mme-gold-deep)",
                color: "var(--mek-text)",
              }}
              data-testid="gift-offer-banner"
            >
              <span aria-hidden="true" style={{ color: "var(--mme-gold-deep)" }}>✦</span>
              <p className="text-sm leading-snug text-left flex-1">
                <span className="font-semibold uppercase tracking-[0.08em] text-[12px]">Limited time —</span>{" "}
                buy one, send one. Share the kit with a colleague at no extra cost.
              </p>
            </div>
          )}

          <div
            className="relative overflow-hidden text-left max-w-xl mx-auto"
            style={{
              background: "#FBF6E8",
              border: "1px solid var(--mek-border)",
              borderRadius: "12px",
              padding: "36px 36px 36px 42px",
            }}
          >
            <span aria-hidden="true" className="absolute left-0 top-0 bottom-0" style={{ width: "5px", background: "#1F4D5A" }} />
            <div className="relative">
              <ul className="space-y-3 mb-8">
                {[
                  "45+ real-world management scenarios",
                  "Web library + downloadable PDF bundle",
                  "Scripts, reminders, and practical follow-through guidance",
                  "Pay once. Come back whenever you need it.",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3 text-prose">
                    <Check size={18} className="mt-1 shrink-0" style={{ color: "#1F4D5A" }} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <label className="eyebrow block mb-2">Your email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@team.com"
                className="mb-6 w-full text-base outline-none transition"
                data-testid="checkout-email-input"
                style={{
                  background: "#ffffff",
                  border: "1px solid var(--mek-border)",
                  borderRadius: "10px",
                  padding: "12px 14px",
                  color: "var(--mek-text)",
                }}
                onFocus={(e) => (e.target.style.borderColor = "var(--mek-text)")}
                onBlur={(e) => (e.target.style.borderColor = "var(--mek-border)")}
              />

              {giftOfferActive && (
                <div
                  className="mb-6 pb-6 border-b"
                  style={{ borderColor: "rgba(31, 77, 90, 0.16)" }}
                >
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeGift}
                      onChange={(e) => setIncludeGift(e.target.checked)}
                      className="mt-1 shrink-0 w-4 h-4 rounded cursor-pointer"
                      style={{ accentColor: "#1F4D5A" }}
                      data-testid="gift-toggle"
                    />
                    <span>
                      <span className="font-semibold text-[15px]" style={{ color: "var(--mek-text)" }}>
                        Also send a free copy to a colleague
                      </span>
                      <span className="block text-sm text-prose mt-0.5">
                        For a limited time, every purchase comes with one to share.
                      </span>
                    </span>
                  </label>

                  {includeGift && (
                    <div className="mt-5 space-y-4" data-testid="gift-fields">
                      <div>
                        <label className="eyebrow block mb-2">Their email</label>
                        <input
                          type="email"
                          value={giftEmail}
                          onChange={(e) => setGiftEmail(e.target.value)}
                          placeholder="colleague@team.com"
                          className="input-line"
                          data-testid="gift-email-input"
                        />
                      </div>
                      <div>
                        <label className="eyebrow block mb-2">Your name (optional)</label>
                        <input
                          type="text"
                          value={senderName}
                          onChange={(e) => setSenderName(e.target.value)}
                          placeholder="Jane Doe"
                          className="input-line"
                          data-testid="gift-sender-input"
                          maxLength={80}
                        />
                      </div>
                      <div>
                        <div className="flex items-baseline justify-between mb-2">
                          <label className="eyebrow">Personal note (optional)</label>
                          <span
                            className="text-[11px] tabular-nums"
                            style={{ color: "var(--mek-text-3)" }}
                          >
                            {giftNote.length}/{GIFT_NOTE_MAX}
                          </span>
                        </div>
                        <textarea
                          value={giftNote}
                          onChange={(e) =>
                            setGiftNote(e.target.value.slice(0, GIFT_NOTE_MAX))
                          }
                          placeholder="A short note they'll see in the email."
                          rows={3}
                          className="w-full p-3 rounded-md border bg-transparent text-prose leading-relaxed text-[15px]"
                          style={{ borderColor: "var(--mek-border)" }}
                          data-testid="gift-note-input"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <button
                onClick={startCheckout}
                disabled={loadingCheckout}
                className="btn-primary w-full"
                data-testid="checkout-button"
              >
                {loadingCheckout
                  ? "Starting checkout…"
                  : includeGift
                  ? `Buy the kit + send a copy — ${formatPrice(priceUsd)}`
                  : `Buy the kit — ${formatPrice(priceUsd)}`}
                {!loadingCheckout && <ArrowRight size={16} />}
              </button>
              <p className="text-xs text-[color:var(--mek-text-3)] mt-4 text-center">
                Secure Stripe checkout. Access link sent to your email instantly{includeGift ? "; theirs sent to them too." : "."}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 md:py-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="h-section text-4xl md:text-5xl mb-12">FAQ.</h2>
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {[
              ["Is this a course?", "No. There are no videos, no modules, no quizzes. It's a reference library you open in the moment — and a PDF you can keep."],
              ["What if I already have a coach or HR partner?", "This kit doesn't replace either. It gives you the language to walk into those conversations more clearly, and to know when to bring HR in."],
              ["Why scenario-based?", "Because management doesn't happen in frameworks, it happens in the moments someone says \"can we talk.\""],
              ["How do I access it after I buy?", "We email you a magic link. Click it, and your library opens. The link works on any device. We'll re-send it whenever you ask."],
              ["Can I purchase multiple licenses for managers within my business?", "Yes — please email hello@managemeantexcellence.com to get the conversation started."],
              ["Is there a refund policy?", "If the kit doesn't help, reply to your purchase email within 14 days. We'll refund you in full, no scripts required."],
              ["Will more scenarios be added?", "Yes — and you'll get them automatically. The library grows as managers ask for the situations they wish were in there."],
            ].map(([q, a], i) => (
              <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`} style={{ borderColor: "var(--mek-border)" }}>
                <AccordionTrigger className="font-serif text-xl text-left hover:no-underline">{q}</AccordionTrigger>
                <AccordionContent className="text-prose text-base">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <p
            className="text-prose text-base mt-10 pt-8 border-t text-center"
            style={{ borderColor: "var(--mek-border)" }}
            data-testid="faq-contact-line"
          >
            Still have a question?{" "}
            <a
              href="mailto:hello@managemeantexcellence.com"
              className="underline hover:no-underline"
              style={{ color: "var(--mme-teal)" }}
            >
              Email hello@managemeantexcellence.com
            </a>{" "}
            — I read every one.
          </p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 md:py-32 text-center" style={{ background: "var(--mme-teal)", color: "#ffffff" }}>
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="h-section text-4xl md:text-6xl mb-8" style={{ color: "#ffffff" }}>
            Management gets hard. <br /><em className="italic whitespace-nowrap" style={{ color: "var(--mme-gold)" }}>You do not have to wing it.</em>
          </h2>
          <a href="#pricing" className="btn-primary" data-testid="final-cta">
            Get Toolkit 3: The Moments
            <ArrowRight size={16} />
          </a>
          <p className="text-sm mt-6" style={{ color: "rgba(255,255,255,0.75)" }}>
            Already bought? <Link to="/access" className="underline" style={{ color: "var(--mme-gold)" }} data-testid="final-already-bought">Get your access link</Link>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
